import { useCallback, useMemo } from "react";
import { UseInitialUserResponse } from "./use-initial-user-query";

export type CanRequest =
  | string
  | {
      or?: CanRequest[];
      and?: CanRequest[];
    };

const useUserPermissions = () => {
  const localPermissions = useMemo(() => {
    return import.meta.glob("@/permissions/*.{ts,tsx,js,jsx,mjs,cjs,mts,cts}", {
      eager: true,
    }) as Record<string, { default: string[] }>;
  }, []);

  const getPermissions = useCallback(
    (user: UseInitialUserResponse) => {
      const { role, permissions: userPermissions = [] } = user;
      const [, rolePermissions] =
        Object.entries(localPermissions).find(([key]) => {
          const match = key.match(/(?:[/\\]?)([^/\\]+?)(?:\.[^.]*)?$/);
          const filename = match?.[1];
          return filename === role;
        }) ?? [];
      const permissionsSet = new Set([
        ...userPermissions,
        ...(rolePermissions?.default ?? []),
      ]);
      const permissions = [...permissionsSet];

      const can = (permission: CanRequest) => {
        if (typeof permission === "string") {
          return permissions.includes(permission);
        }

        return (
          (!permission.and || permission.and.every(can)) &&
          (!permission.or || permission.or.some(can))
        );
      };

      return {
        can,
        permissions,
      };
    },
    [localPermissions]
  );

  return getPermissions;
};

export default useUserPermissions;
